import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useBlocker } from 'react-router-dom';
import { useUnloadGuard } from '../../../shared/hooks';
import { groupCadenceItemsIntoSteps } from '../lib/groupCadenceItemsIntoSteps';
import { cadenceStepsToPayload } from '../lib/cadenceStepMoves';
import { reorderCampaignCadence } from '../store/campaignCadencesThunks';

export function useCampaignCadencesEditing(campaignCadence, languages) {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [localSteps, setLocalSteps] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingWithSave, setPendingWithSave] = useState(false);

  const savedSteps = useMemo(
    () => groupCadenceItemsIntoSteps(campaignCadence?.items ?? []),
    [campaignCadence?.items],
  );

  const localPayload = useMemo(
    () => cadenceStepsToPayload(localSteps, languages),
    [localSteps, languages],
  );

  const hasChanges = useMemo(() => {
    if (!isEditing) return false;
    const saved = cadenceStepsToPayload(savedSteps, languages);
    return JSON.stringify(saved) !== JSON.stringify(localPayload);
  }, [isEditing, savedSteps, languages, localPayload]);

  const blocker = useBlocker(isEditing && hasChanges);

  // Reload / close tab / URL bar leave the document, so useBlocker can't hold them — same dirty
  // condition, so only a pending reorder prompts.
  useUnloadGuard(isEditing && hasChanges);

  const discardOpen = pendingAction !== null || blocker.state === 'blocked';

  const guardAction = useCallback(
    (action, withSave = false) => {
      if (isEditing && hasChanges) {
        setPendingAction(() => action);
        setPendingWithSave(withSave);
      } else {
        setIsEditing(false);
        action();
      }
    },
    [isEditing, hasChanges],
  );

  const handleEdit = useCallback(() => {
    setLocalSteps(savedSteps);
    setIsEditing(true);
  }, [savedSteps]);

  const handleSave = useCallback(async () => {
    if (!campaignCadence) return;
    await dispatch(
      reorderCampaignCadence({ campaignCadenceId: campaignCadence.id, steps: localPayload }),
    );
    setIsEditing(false);
  }, [dispatch, campaignCadence, localPayload]);

  const handleDiscardConfirm = useCallback(() => {
    setIsEditing(false);
    setPendingWithSave(false);
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else {
      const action = pendingAction;
      setPendingAction(null);
      action?.();
    }
  }, [blocker, pendingAction]);

  const handleSaveAndProceed = useCallback(async () => {
    if (!campaignCadence) return;
    try {
      await dispatch(
        reorderCampaignCadence({ campaignCadenceId: campaignCadence.id, steps: localPayload }),
      ).unwrap();
      setIsEditing(false);
      setPendingWithSave(false);
      if (blocker.state === 'blocked') {
        blocker.proceed();
      } else {
        const action = pendingAction;
        setPendingAction(null);
        action?.();
      }
    } catch {}
  }, [dispatch, campaignCadence, localPayload, blocker, pendingAction]);

  const handleDiscardClose = useCallback(() => {
    setPendingWithSave(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    } else {
      setPendingAction(null);
    }
  }, [blocker]);

  const showSaveOnDiscard = pendingWithSave || blocker.state === 'blocked';

  return {
    isEditing,
    savedSteps,
    localSteps,
    setLocalSteps,
    hasChanges,
    discardOpen,
    showSaveOnDiscard,
    guardAction,
    handleEdit,
    handleSave,
    handleDiscardConfirm,
    handleDiscardClose,
    handleSaveAndProceed,
  };
}
